import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DocumentVente from './DocumentVente';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import { AppDispatch } from '../reduxStore/Store';
import { fetchDocumentById, setSelectedDocument } from '../reduxStore/documentVenteSlice';
import { labels } from '../constants/labels';

/**
 * Détail/édition d'un document de vente — route dédiée (/facturation/:id).
 *
 * `:id` est la clé numérique du document (jamais `numero_doc`, qui n'est unique
 * que par (owner_id, doc_code, departement_id)). Si le document sélectionné en
 * Redux ne correspond pas déjà à cet id, on le charge via le thunk dédié
 * `fetchDocumentById` (GET /documentVentes/:id).
 *
 * Réutilise tel quel le composant `DocumentVente` (lignes, clôture, impression)
 * qui ne lit que `selectedDocument` depuis Redux et ne fait aucun fetch lui-même.
 *
 * Accessible depuis plusieurs origines (liste de documents, mouvements de stock,
 * et potentiellement d'autres écrans à l'avenir) : le retour utilise l'historique
 * de navigation (`navigate(-1)`) pour revenir exactement là d'où on vient, plutôt
 * qu'un chemin fixe vers la liste. Repli sur `/facturation` seulement s'il n'y a
 * aucun historique (arrivée directe par URL/deep-link).
 */
const DocumentVenteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedDocument = useSelector((state: any) => state.DocumentVenteSlice.selectedDocument);

  useEffect(() => {
    if (!id || Number.isNaN(numericId)) return;
    if (!selectedDocument || selectedDocument.id !== numericId) {
      // Annulé si on quitte la page ou si l'id change : une réponse tardive
      // écraserait sinon selectedDocument sur l'écran suivant (ex : création).
      const request = dispatch(fetchDocumentById(numericId));
      return () => request.abort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId, selectedDocument, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedDocument(null));
    };
  }, [dispatch]);

  const handleRetour = () => {
    // location.key === 'default' : pas d'historique dans cette session (deep-link,
    // arrivée directe par URL) — navigate(-1) sortirait de l'app. Repli sur la liste.
    if (location.key === 'default') navigate('/facturation');
    else navigate(-1);
  };

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={selectedDocument
          ? `Modifier ${selectedDocument.type_document?.libelle ?? labels.documentVente}`
          : labels.documentVente}
        icon={<ReceiptLongIcon />}
        onBack={handleRetour}
        backLabel="Retour"
      />
      <AppSurface>
        <DocumentVente />
      </AppSurface>
    </AppPage>
  );
};

export default DocumentVenteDetailPage;
